import { getServiceIcon } from "Components/ServiceIcon/helper";
import { roundedNumber, sortAlphabetically } from "Libs/utils";

import type {
  AddClassProps,
  ReturnAddClass,
  ResourceCollectionType,
  TotalResources
} from "./types";
import type {
  Deployment,
  DeploymentService,
  DeploymentServices
} from "Api/organizations/projects/environments/deployments/types";

export const addClass = (
  list: AddClassProps["list"] = {},
  newClass: AddClassProps["newClass"]
) => {
  return Object.keys(list).reduce<ReturnAddClass>((current, key) => {
    const icon = getServiceIcon(list[key]).iconName;
    const name = list[key]?.name || key;

    current[key] = { icon, class: newClass, ...list[key], name };
    return current;
  }, {});
};

export const addClassWorker = (
  list: [string, DeploymentService][] = [],
  newClass: AddClassProps["newClass"]
) => {
  return list.reduce<ReturnAddClass>((acc, [key, value], index) => {
    const icon = getServiceIcon(value).iconName;
    const name = value?.name || key;

    acc[index] = { icon, class: newClass, ...value, name };

    return acc;
  }, {});
};

export const retrieveOneResourceRawDataHandler = ({
  currentDeployment,
  resourceType,
  appName
}: {
  currentDeployment: Deployment | undefined;
  resourceType: ResourceCollectionType;
  appName: string;
}) => {
  const allProfiles = currentDeployment?.container_profiles;
  const resource = currentDeployment?.[resourceType];
  const resourceApp = resource?.[appName];
  const cronsQuantity = Object.keys(resourceApp?.crons ?? {}).length;
  const profileSize = resourceApp?.resources?.profile_size;
  const containerProfile = resourceApp?.container_profile;
  const currentProfile = allProfiles?.[containerProfile!];
  const cpu = currentProfile?.[profileSize!].cpu ?? 0;
  const ram = currentProfile?.[profileSize!].memory ?? 0;
  const disk = resourceApp?.disk ?? 0;
  const instances = resourceApp?.instance_count ?? 1;
  const status = currentDeployment?.environment_info?.status ?? "";

  return { cpu, ram, instances, disk, status, cronsQuantity, containerProfile };
};

export const resourcesAccumulatorHandler = (
  totalResources: TotalResources,
  resourceItem: { [K in keyof TotalResources]?: TotalResources[K] | null }
): TotalResources & { status: string } => {
  const cpu = resourceItem?.cpu ?? 0;
  const ram = resourceItem?.ram ?? 0;
  const instances = resourceItem?.instances ?? 1;
  const disk = resourceItem?.disk ?? 0;
  const status = resourceItem?.status ?? "";

  return {
    cpu: totalResources.cpu + cpu * instances,
    ram: totalResources.ram + ram * instances,
    instances: totalResources.instances + instances,
    disk: totalResources.disk + disk,
    status
  };
};

export const resourcesFormatHandler = (
  totalResources: TotalResources
): TotalResources => ({
  cpu: roundedNumber(totalResources.cpu, 3),
  ram: roundedNumber(totalResources.ram / 1024, 3),
  instances: totalResources.instances,
  disk: roundedNumber(totalResources.disk / 1024, 3),
  status: totalResources.status
});

export const deploymentResourcesHandler = (deployment?: Deployment) => {
  let totalResources = {
    cpu: 0,
    ram: 0,
    instances: 0,
    disk: 0,
    status: ""
  };
  const { webapps, workers, services } = deployment ?? {
    webapps: undefined,
    workers: undefined,
    services: undefined
  };
  const status = deployment?.environment_info?.status ?? "";

  try {
    Object.values({ webapps, workers, services }).forEach(
      resourceCollection => {
        Object.values(resourceCollection ?? {}).forEach(data => {
          const containerProfiles =
            deployment?.container_profiles[data.container_profile ?? ""];

          const cpu =
            containerProfiles?.[data?.resources.profile_size ?? ""].cpu;
          const ram =
            containerProfiles?.[data?.resources.profile_size ?? ""].memory;
          const { disk, instance_count: instances } = data;

          totalResources = resourcesAccumulatorHandler(totalResources, {
            cpu,
            ram,
            instances,
            disk,
            status
          });
        });
      }
    );

    return resourcesFormatHandler(totalResources);
  } catch {
    return { cpu: 0, ram: 0, instances: 0, disk: 0, status };
  }
};

export const transformServices = (
  services: DeploymentServices,
  kind: "webapp" | "service" | "worker"
) =>
  sortAlphabetically(
    Object.entries(services).map(([key, value]) => ({
      ...value,
      icon: value.type.split(":")[0],
      name: key,
      kind
    })),
    service => service.name
  );
